import React, { useEffect, useState } from "react";

import BigBox from "../../../Universals/DragBox/BigBox/bigBox.component.jsx";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { useNavBar } from "../../../../Contexts/navbar.items.context.jsx";

import NoteList from "./noteList.component.jsx";
import NoteDisplayPane from "./noteDisplay.component.jsx";
import NewNoteComponent from "./createNewNote.component.jsx";
import { getUserFromToken } from "../../../../utils/functions.ts";
import { getTodayEasternYMD } from "../../../../utils/dateUtils.ts";
import { buildPlayerWindowTitle } from "../../../../utils/playerTitle.js";

const NOTES_ENDPOINT = "https://www.fbxchange.com/ReactPHP/notes.php";
const SCRIPT_DELETE_NOTE = "deleteNote";
const DIRTY_EVENT = "FBX_NOTE_EDITOR_DIRTY";


const getTodayLocalYMD = () => getTodayEasternYMD();

export default function NoteBase({ onClose = () => { } }) {
  const { updateActiveSideButton } = useNavBar() || {};

  const playerCtx = usePlayer() || {};
  const activePlayer = playerCtx.activePlayer;
  const playerData = playerCtx.playerData;
  const playerID = playerCtx.playerID;

  const setActivePlayerFn =
    playerCtx.setActivePlayer ||
    playerCtx.updateActivePlayer ||
    playerCtx.updatePlayer ||
    playerCtx.setPlayer;

  const setPlayerIDFn =
    playerCtx.setPlayerID ||
    playerCtx.updatePlayerID ||
    playerCtx.updatePlayerId ||
    playerCtx.updatePlayerIDValue;

  const setPlayerDataFn =
    playerCtx.setPlayerData ||
    playerCtx.updatePlayerData ||
    playerCtx.setPlayerArray ||
    playerCtx.updatePlayerArray;

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [notesDirty, setNotesDirty] = useState(false);

  const [newNote, setNewNote] = useState({
    SourceData: "",
    DateOfContact: getTodayLocalYMD(),
    isPrivate: true,
  });


  useEffect(() => {
    if (!activePlayer?.FBXID && !playerID) return;

    setShowCreateForm(false);
    setNewNote((prev) => ({ ...prev, DateOfContact: getTodayLocalYMD() }));
    setRefreshKey((prev) => prev + 1);
  }, [activePlayer?.FBXID, playerID]);

  useEffect(() => {
    const handleDirtyEvent = (e) => {
      const dirty = !!e?.detail?.dirty;
      setNotesDirty(dirty);
    };

    window.addEventListener(DIRTY_EVENT, handleDirtyEvent);
    return () => {
      window.removeEventListener(DIRTY_EVENT, handleDirtyEvent);
    };
  }, []);

  const handleCloseNotes = () => {
    if (typeof updateActiveSideButton === "function") updateActiveSideButton(null);
    if (typeof onClose === "function") onClose();
  };

  const handlePlayerClick = (player) => {
    if (!player || notesDirty) return;

    if (typeof setActivePlayerFn === "function") setActivePlayerFn(player);
    if (typeof setPlayerIDFn === "function") setPlayerIDFn(player.FBXID);

    setRefreshKey((prev) => prev + 1);
  };

  const newFormButton = () => {
    setNewNote({
      SourceData: "",
      DateOfContact: getTodayLocalYMD(),
      isPrivate: true,
    });
    setShowCreateForm(true);
  };

  const handleNewCancel = () => {
    setShowCreateForm(false);
    setRefreshKey((prev) => prev + 1);
  };

  const patchNoteCountInContext = (FBXID, newCount) => {
    const nextCountStr = String(newCount ?? 0);

    if (Array.isArray(playerData) && typeof setPlayerDataFn === "function") {
      const updated = playerData.map((p) =>
        String(p?.FBXID) === String(FBXID) ? { ...p, note: nextCountStr } : p
      );
      setPlayerDataFn(updated);
    }

    const cur = activePlayer;
    if (cur && String(cur?.FBXID) === String(FBXID) && typeof setActivePlayerFn === "function") {
      setActivePlayerFn({ ...cur, note: nextCountStr });
    }
  };

  const extractNotesArray = (data) => {
    if (Array.isArray(data?.notes?.notes)) return data.notes.notes;
    if (Array.isArray(data?.notes)) return data.notes;
    if (Array.isArray(data)) return data;
    return [];
  };

  const handleCreateNewNote = async () => {
    const FBXID = activePlayer?.FBXID || playerID;
    if (!FBXID) return;

    const token = localStorage.getItem("user2026");
    if (!token) {
      console.error("No token found for user identification");
      alert("No login token found. Please log in again.");
      return;
    }

    const user = await getUserFromToken().catch(() => null);
    if (!user) {
      console.error("Failed to get user from token");
      alert("Could not read user session. Please log in again.");
      return;
    }

    const payload = {
      scriptName: "createNote",
      scout: user.scout,
      team: user.team,
      createdBy: user.displayName,
      FBXID,
      SourceData: newNote?.SourceData ?? "",
      DateOfContact: newNote?.DateOfContact ?? getTodayLocalYMD(),
      isPrivate: newNote?.isPrivate ? "Y" : "N",
    };

    try {
      const response = await fetch(NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const rawText = await response.text().catch(() => "");
      if (!response.ok) {
        console.error("Create note failed:", response.status, rawText);
        alert("Failed to create note. Please try again.");
        return;
      }

      let data;
      try {
        data = JSON.parse(rawText);
      } catch (e) {
        console.error("Create note returned non-JSON:", rawText);
        alert("Create note failed (bad response). Please try again.");
        return;
      }

      const notes = extractNotesArray(data);
      patchNoteCountInContext(FBXID, notes.length);

      const highestId =
        Array.isArray(notes) && notes.length
          ? Math.max(...notes.map((n) => Number(n?.id) || 0))
          : null;

      if (highestId) {
        const TOKEN_KEY = "noteToken";
        const today = getTodayEasternYMD();

        let tokenArr = [];
        try {
          const raw = localStorage.getItem(TOKEN_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            tokenArr = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
          }
        } catch (e) {
          console.warn("Failed to parse noteToken:", e);
          tokenArr = [];
        }

        const idx = tokenArr.findIndex((r) => String(r?.FBXID) === String(FBXID));
        if (idx !== -1) {
          tokenArr[idx] = { ...tokenArr[idx], FBXID, NoteID: highestId, NoteDate: today };
        } else {
          tokenArr.push({ FBXID, NoteID: highestId, NoteDate: today });
        }

        localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenArr));
      }

      setShowCreateForm(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error creating note:", error);
      alert("An error occurred while creating the note. Please try again.");
    }
  };

  const handleDeleteNote = async ({ noteID, FBXID, team }) => {
    if (!noteID) return { ok: false, error: "Missing noteID" };

    const user = await getUserFromToken().catch(() => null);
    if (!user) {
      console.error("Failed to get user from token");
      alert("Could not read user session. Please log in again.");
      return;
    }

    try {
      const res = await fetch(NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: SCRIPT_DELETE_NOTE,
          noteID: Number(noteID),
          FBXID: FBXID ?? "",
          team: team ?? "",
          scout: user?.scout ?? "",
        }),
      });

      const rawText = await res.text().catch(() => "");
      if (!res.ok) {
        console.error("Delete failed:", res.status, rawText);
        return { ok: false, error: `Delete failed (HTTP ${res.status})` };
      }

      let data;
      try {
        data = JSON.parse(rawText);
      } catch (e) {
        console.error("Delete returned non-JSON:", rawText);
        return { ok: false, error: "Delete failed (bad response)" };
      }

      const notes = extractNotesArray(data);
      if (FBXID) patchNoteCountInContext(FBXID, notes.length);

      return { ok: true, data, notes };
    } catch (err) {
      console.error(err);
      return { ok: false, error: err?.message || "Delete failed." };
    }
  };

  const letGoToReport = () => {
    updateActiveSideButton("reports1");
  };

  const letGoToFiles = () => {
    updateActiveSideButton("Files");
  };

  const currentFBXID = activePlayer?.FBXID || playerID || null;
  const notesTitle = buildPlayerWindowTitle(activePlayer, "Notes");

  const sideMask = notesDirty ? (
    <div
      className="position-absolute top-0 start-0 w-100 h-100"
      style={{
        zIndex: 50,
        background: "rgba(255,255,255,0.45)",
        cursor: "not-allowed",
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    />
  ) : null;

  return (
    <BigBox
      storageKey="NOTE_BOX_POSITION"
      defaultPos={{ x: 60, y: 100 }}
      title={notesTitle}
      width={1820}
      zIndex={2050}
      closeButton={true}
      onAddText="New Note"
      addNote="Create New Note"
      onAddFunction={newFormButton}
      onCloseButton={handleCloseNotes}
      showThirdButton={true}
      thirdButtonText="View Reports"
      thirdButtonClick={() => letGoToReport()}
      showFourthButton={true}
      fourthButtonText="View Files"
      fourthButtonClick={() => letGoToFiles()}
      backgroundColor="#e1ebf5ff"
      showListDropdown={true}
      mask={notesDirty}
    >
      <div className="h-100" style={{ minHeight: 0 }}>
        {!showCreateForm ? (
          <div className="d-flex gap-3 h-100" style={{ minHeight: 0, overflow: "hidden" }}>
            <div className="col-14 d-flex flex-column position-relative h-100" style={{ minHeight: 0, overflow: "hidden" }}>
              <div
                className="border rounded d-flex flex-column position-relative"
                style={{ flex: "0 0 305px", minHeight: 0, overflow: "hidden" }}
              >
                {sideMask}
                <NoteList
                  height={305}
                  refreshKey={refreshKey}
                  FBXID={currentFBXID}
                  key={`${currentFBXID || "noPlayer"}-${refreshKey}`}
                />
              </div>

              <div
                className="mt-2 flex-grow-1 position-relative border rounded"
                style={{ minHeight: 0, overflowY: "auto" }}
              >
                {sideMask}

                {!playerData ? (
                  <div className="p-2 fs-7">Loading player data...</div>
                ) : playerData.length === 0 ? (
                  <div className="p-2 fs-7">No players in dataset</div>
                ) : (
                  <div className="w-100 fs-7">
                    <div
                      className="d-flex gap-2 bg-navy fs-7"
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        background: "var(--navy, #001f3f)",
                        height: "35px",
                        alignItems: "center",
                      }}
                    >
                      <div className="flex-grow-1 text-lemon ps-2">Name</div>
                      <div className="text-center col-5 text-lemon">School</div>
                      <div className="text-center col-5 text-lemon">CGP</div>
                      <div className="text-center col-5 text-lemon">Col#</div>
                      <div className="text-center col-5 text-lemon">Pro#</div>
                      <div className="text-center col-5 text-lemon">RNF</div>
                    </div>

                    {playerData.map((p, i) => {
                      const isSelected = p.FBXID === playerID || p.FBXID === activePlayer?.FBXID;

                      return (
                        <div
                          key={p.FBXID ?? i}
                          className={`d-flex fs-9 gap-2 py-1 align-items-center ${isSelected ? "alert-danger text-black" : ""}`}
                          style={{
                            borderBottom: "1px solid #ccc",
                            height: "35px",
                            cursor: notesDirty ? "not-allowed" : "pointer",
                            color: isSelected ? "#000" : undefined,
                          }}
                          onClick={() => handlePlayerClick(p)}
                        >
                          <div
                            className={`flex-grow-1 text-truncate ps-2
                            ${p.impnip === "G" ? "globalPlayer bg-info-subtle" : p.impnip?.startsWith("V") ? "brownPlayer bg-warning-subtle" : ""}
                            ${p.impnip === "N" ? "text-danger" : ""}`}
                          >
                            {`${(p.last || "").toUpperCase()}, ${(p.first || "").toUpperCase()}`}
                          </div>

                          <div className="col-5 text-center">{p.school ?? "-"}</div>
                          <div className="col-5 text-center">{p.genPos ?? "-"}</div>
                          <div className="col-5 text-center">{p.jersey === "-1" ? "0" : p.jersey ?? "-"}</div>
                          <div className="col-5 text-center">{p.proJersey ?? "-"}</div>
                          <div className="col-5 text-center">
                            {parseInt(p.report) === 0 ? "-" : p.report}{" "}
                            {parseInt(p.note) === 0 ? "-" : p.note ?? "-"}{" "}
                            {parseInt(p.pdf) === 0 ? "-" : p.pdf ?? "-"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="w-100 h-100" style={{ minHeight: 0, overflow: "hidden" }}>
              <div className="container-fluid p-0 h-100" style={{ minHeight: 0 }}>
                <div className="row justify-content-center h-100" style={{ minHeight: 0 }}>
                  <div
                    className="col-48 bg-white rounded shadow-sm p-0 d-flex flex-column h-100"
                    style={{ minHeight: 0, overflow: "hidden" }}
                  >
                    <div className="px-2 pb-3 d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
                      <div
                        className="mt-2 border w-100 border-rounded d-flex flex-column"
                        style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
                      >
                        <div className="p-3 text-muted">
                          <NoteDisplayPane
                            height="100%"
                            FBXID={currentFBXID}
                            refreshKey={refreshKey}
                            letsDeleteNote={handleDeleteNote}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <NewNoteComponent
            activePlayer={activePlayer}
            newNote={newNote}
            onChange={(field, value) => setNewNote((prev) => ({ ...prev, [field]: value }))}
            onTogglePrivacy={(isPrivate) => setNewNote((prev) => ({ ...prev, isPrivate }))}
            onToday={() => setNewNote((prev) => ({ ...prev, DateOfContact: getTodayLocalYMD() }))}
            onCancel={handleNewCancel}
            onSave={handleCreateNewNote}
          />
        )}
      </div>
    </BigBox>
  );
}
